import parentApi from "store/parentApi"
import { convertQueryObjToString, localisedValue } from "utils"
import { dateTimeFunction } from "utils/date"
import inquiryStatusTransformer from "./inquiryStatusTransformer"
import lmsProjectTransformer from "./lmsProjectTransformer"
import campaignAddtitionalFieldTransformer from "./campaignAddtitionalFieldTransformer"
import fetchLeadsTransformer from "./fetchLeadTransformer"
import lmsColumnsTransformer from "./lmsColumnsTransformer"
import { truncateText } from "utils/reports"
import dayjs from "dayjs"

const lmsLeadsApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getLmsLeads: build.query({
      query: params => {
        let obj = { ...params }
        const checkDate = key => {
          if (!!obj[key]) {
            const dateR = obj[key].split(",")
            obj[key] = encodeURIComponent(
              `{"from":"${dateTimeFunction({
                date: dateR[0],
                timeZone: true,
                apiRequest: true,
              })}","to":"${dateTimeFunction({
                date: dateR[1],
                timeZone: true,
                apiRequest: true,
              })}"}`
            )
          }
        }
        const checkUsers = key => {
          if (!!obj[key]) {
            let childIdentifierKey = key + "_withoutChildren"
            obj[key] = encodeURIComponent(
              `{"teamIds":[],"userIds":[${decodeURIComponent(obj[key])}],"includeAllChildren":${!obj?.[
                childIdentifierKey
              ]}}`
            )
          }
        }

        checkDate("createdAt")
        checkDate("dateOfLead")
        checkDate("updatedAt")
        checkUsers("lastUpdatedBy")
        checkUsers("assigneeId")
        checkUsers("addedBy")

        return `/externalInquiries/search/v2?${convertQueryObjToString({
          page: 0,
          pageLimit: 10,
          sortBy: "updatedAt",
          sortOrder: "desc",
          tab: "all",
          ...obj,
        })}`
      },
      providesTags: ["lms-leads"],
    }),
    getFetchLead: build.query({
      query: () => `/externalInquiries/fetch`,
      transformResponse: response => fetchLeadsTransformer(response),
    }),
    getLmsPlatform: build.query({
      query: () => `/marketing/platforms`,
      transformResponse: data =>
        data?.map(item => ({
          ...item,
          label: localisedValue({ object: item, key: "title" }),
          value: item?.id?.toString(),
        })),
    }),
    getLmsPools: build.query({
      query: () => `/inquiryPools`,
      transformResponse: data =>
        data.data?.map(item => ({
          ...item,
          label: localisedValue({ object: item, key: "name" }),
          value: item?.id?.toString(),
        })),
    }),
    getLmsCampaigns: build.query({
      query: params => `/marketing/campaigns?${convertQueryObjToString(params)}`,
      transformResponse: data =>
        data?.campaignData.map(item => ({
          ...item,
          label: truncateText(localisedValue({ object: item, key: "campaignName" }), 45),
          completeLabel: localisedValue({ object: item, key: "campaignName" }),
          value: item?.id.toString(),
        })),
    }),
    getInquiryStatuses: build.query({
      query: () => `/externalInquiryStatuses`,
      transformResponse: data => inquiryStatusTransformer(data),
    }),
    getLmsProjects: build.query({
      query: id => `/projects/detail/${id}`,
      transformResponse: data => lmsProjectTransformer(data),
    }),
    getLeadHistoryLogs: build.query({
      query: ({ id, page, pageLimit }) => {
        return `externalInquiries/${id}/actionLogs?${convertQueryObjToString({ page, pageLimit })}`
      },
    }),

    addUpdateExternalLead: build.mutation({
      query: e => {
        let body = {
          ...(e?.project && { projectId: parseInt(e?.project) }),
          ...(e?.partner && { partnerId: parseInt(e?.partner) }),
          campaignId: e?.campaign,
          clientName: e?.clientName,
          clientCell: e?.clientCell,
          ...(e?.clientEmail && { clientEmail: e?.clientEmail }),
          ...(e?.clientNationality && { clientNationality: e?.clientNationality }),
          clientCountryId: e?.clientCountryId,
          clientCityId: e?.clientCityId,
          ...(!e?.id && { inquiryType: e?.leadType }),
          clientTypeId: e?.clientType || null,
          statusId: e?.inquiryId,
          ...(e?.reason && { statusChangeReasonId: e?.reason }),
          changeAssigneeProcess: e?.reallocatedType,
          ...(e?.reallocatedType === "manual" && { selectedAssigneeId: e?.allocatedTo }),
          classificationId: e?.classificationId,
          socialPlatformId: e?.platform,
          sourceId: e?.source,
          ...(e?.deadLine && {
            statusDeadline: dateTimeFunction({ date: e?.deadLine, apiRequest: true, timeZone: true }),
          }),
          ...(e?.description && { statusChangeComment: e?.description }),
          ...(e?.additionalData && { additionalData: e?.additionalData }),
        }
        return {
          url: e?.id ? `/externalInquiries/${e?.id}` : `/externalInquiries`,
          method: e?.id ? "PUT" : "POST",
          body: body,
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["lms-leads"] : []
      },
    }),

    updateMarketingLeadsStatus: build.mutation({
      query: e => {
        let body = {
          ...(e?.reallocatedType && { allocationProcess: e?.reallocatedType }),
          ...(e?.classificationId && { classificationId: e?.classificationId }),
          ...(e?.allocatedTo && { userIds: Array.isArray(e?.allocatedTo) ? e?.allocatedTo : [e?.allocatedTo] }),
          status: e?.status,
          statusChangeReasonId: e?.reason,
          statusChangeComment: e?.description,
          ids: e?.ids,
        }
        return {
          url: `/externalInquiries/changeStatus`,
          method: "PUT",
          body: body,
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["lms-leads"] : []
      },
    }),

    importLeads: build.mutation({
      query: params => {
        let body = {
          s3FileKey: params?.leadsFile?.fileList?.[0]?.response?.key,
          validateOnly: params.validateOnly,
          campaignId: params?.campaignId,
        }
        return { url: "/externalInquiries/import", method: "POST", body: body }
      },
      invalidatesTags: (result, error, args) => {
        return !error && !args.validateOnly ? ["lms-leads"] : []
      },
    }),
    getCampaignAdditionalFields: build.query({
      query: id => `/marketing/campaigns/${id}`,
      transformResponse: resp => campaignAddtitionalFieldTransformer(resp),
      providesTags: ["additional-fields"],
    }),
    getSampleImportFile: build.mutation({
      query: param => {
        return {
          url: `/externalInquiries/export`,
          method: "POST",
          body: { campaignIds: [param.id] },
        }
      },
    }),
    getCampaignList: build.query({
      query: () => "/marketing/campaigns",
      transformResponse: resp =>
        resp?.campaignData?.map(campaign => ({ value: campaign?.id, label: campaign?.campaignName })),
      providesTags: ["campaign-listing"],
    }),
    getColumns: build.query({
      query: () => "externalInquiries/displayColumns",
      transformResponse: resp =>
        resp?.map(item => ({ title: item?.columnName, key: item?.id, isAdditional: item?.isAdditional })),
    }),
    getUserSelectedColumns: build.query({
      query: () => "externalInquiries/userSelectedColumns",
      transformResponse: resp => lmsColumnsTransformer(resp),
      providesTags: ["user-columns"],
    }),
    updateUserSelection: build.mutation({
      query: params => {
        return {
          url: `externalInquiries/userSelectedColumns`,
          method: params?.length ? "PUT" : "DELETE",
          ...(params?.length && {
            body: {
              updatedColumns: params?.map((column, index) => ({
                externalInquiryDisplayColumnId: column,
                columnSequence: index + 1,
              })),
            },
          }),
        }
      },
      invalidatesTags: (result, error, args) => {
        return !error ? ["lms-leads", "user-columns"] : []
      },
    }),
    callViaMaqsam: build.mutation({
      query: values => {
        return {
          url: `externalInquiryPhoneCalls/initiateCall`,
          method: "POST",
          body: values,
        }
      },
    }),
    changeExternalInquiriesAssignee: build.mutation({
      query: ({ externalInquiryIds, type, ...values }) => {
        const body = {
          ...(values?.allocatedTo && {
            userIds: Array.isArray(values?.allocatedTo)
              ? values?.allocatedTo?.map(item => item.id)
              : [values?.allocatedTo]?.map(item => item?.id),
          }),
          changeAssigneeProcess: values?.requestReAllocation
            ? "request_re_allocation"
            : values?.reallocatedType
              ? values?.reallocatedType
              : null,
          comments:
            values?.reallocatedType === "manual"
              ? "Reassigning to specific user"
              : "Distributing leads using round-robin",
          externalInquiryIds: externalInquiryIds,
          ...(values?.manualAllocationReason && { manualAllocationReason: values.manualAllocationReason }),
        }
        return { url: "/externalInquiries/changeAssignee", method: "PUT", body: body }
      },
      invalidatesTags: (resp, error, args) => (resp ? ["lms-leads"] : []),
    }),
  }),
})

export const {
  useGetLmsCampaignsQuery,
  useGetLmsProjectsQuery,
  useLazyGetLmsCampaignsQuery,
  useGetLmsPlatformQuery,
  useGetLmsPoolsQuery,
  useGetLmsLeadsQuery,
  useGetInquiryStatusesQuery,
  useLazyGetInquiryStatusesQuery,
  useLazyGetLmsProjectsQuery,
  useLazyGetLeadHistoryLogsQuery,
  useAddUpdateExternalLeadMutation,
  useLazyGetFetchLeadQuery,
  useLazyGetLmsPlatformQuery,
  useUpdateMarketingLeadsStatusMutation,
  useImportLeadsMutation,
  useLazyGetCampaignAdditionalFieldsQuery,
  useGetSampleImportFileMutation,
  useLazyGetCampaignListQuery,
  useGetColumnsQuery,
  useGetUserSelectedColumnsQuery,
  useUpdateUserSelectionMutation,
  useCallViaMaqsamMutation,
  useChangeExternalInquiriesAssigneeMutation,
} = lmsLeadsApi
